import type { OrderActivityLogDetail, OrderActivityLogListItem } from '@/models/OrderActivityLog';
import { useMainStore } from '@/store';
import { useQuery, type UseQueryReturnType } from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

function isValidId(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

export function useOrderActivityLogs(
  orderItemId: MaybeRefOrGetter<number | null | undefined>,
): { listQuery: UseQueryReturnType<OrderActivityLogListItem[], Error> } {
  const store = useMainStore();

  const listQuery = useQuery({
    queryKey: computed(() => ['order-activity-logs', toValue(orderItemId)] as const),
    queryFn: async () => {
      const id = toValue(orderItemId);
      if (!isValidId(id)) return [];
      const rows = await store.getOrderActivityLogs(id);
      return rows ?? [];
    },
    enabled: computed(() => isValidId(toValue(orderItemId))),
  });

  return { listQuery };
}

export function useOrderActivityLogDetail(
  orderItemId: MaybeRefOrGetter<number | null | undefined>,
  activityLogId: MaybeRefOrGetter<string | null | undefined>,
): { detailQuery: UseQueryReturnType<OrderActivityLogDetail | null, Error> } {
  const store = useMainStore();

  const detailQuery = useQuery({
    queryKey: computed(() => [
      'order-activity-log-detail',
      toValue(orderItemId),
      toValue(activityLogId),
    ] as const),
    queryFn: async () => {
      const oiid = toValue(orderItemId);
      const lid = toValue(activityLogId);
      if (!isValidId(oiid) || !lid) return null;
      return store.getOrderActivityLogDetail(oiid, lid);
    },
    enabled: computed(() => {
      const oiid = toValue(orderItemId);
      const lid = toValue(activityLogId);
      return isValidId(oiid) && typeof lid === 'string' && lid.length > 0;
    }),
    /** One fetch per (orderItemId, activityLogId); revisit uses cache (invalidate elsewhere if needed). */
    staleTime: Infinity,
    gcTime: Infinity,
  });

  return { detailQuery };
}
